import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import HelpTooltip from "components/HelpTooltip";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import Footer from "components/layout/Footer";

const tutorialContentStyles = ({
  palette: { dark, grey, text },
  typography: { fontFamily, fontWeightBold, size },
}) => ({
  maxWidth: "64rem",
  mx: "auto",
  color: text.main,
  fontFamily,
  fontSize: size.md,
  lineHeight: 1.8,
  "& h4": {
    maxWidth: "72ch",
    margin: "2rem 0 0.75rem",
    color: dark.main,
    fontFamily,
    fontSize: size.xl,
    fontWeight: fontWeightBold,
    lineHeight: 1.4,
  },
  "& h5": {
    maxWidth: "72ch",
    margin: "1.75rem 0 0.5rem",
    color: dark.main,
    fontFamily,
    fontSize: size.md,
    fontWeight: fontWeightBold,
    letterSpacing: "0.01em",
    lineHeight: 1.5,
  },
  "& h6": {
    maxWidth: "72ch",
    margin: "1.25rem 0 0.5rem",
    color: dark.main,
    fontFamily,
    fontSize: size.sm,
    fontWeight: fontWeightBold,
    lineHeight: 1.5,
  },
  "& h4:first-of-type": {
    marginTop: 0,
  },
  "& p": {
    maxWidth: "72ch",
    margin: "0 0 1rem",
    fontSize: size.md,
    lineHeight: 1.8,
  },
  "& ul": {
    margin: "0 0 1rem",
    paddingLeft: "1.5rem",
  },
  "& li": {
    marginBottom: "0.5rem",
    lineHeight: 1.7,
  },
  "& .tutorial-table-scroll": {
    width: "100%",
    margin: "1rem 0 2rem",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  "& table": {
    minWidth: "42rem",
    width: "100%",
    border: "none",
    borderCollapse: "collapse",
  },
  "& th, & td": {
    padding: "0.75rem 0.875rem",
    border: "1px solid " + grey[300],
    textAlign: "left",
    verticalAlign: "top",
    lineHeight: 1.5,
  },
  "& th": {
    backgroundColor: grey[100],
    color: dark.main,
    fontSize: size.sm,
    fontWeight: fontWeightBold,
  },
});

function TutorialList({ items }) {
  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

function TutorialTable({ title, description, table }) {
  return (
    <>
      <h6>{title}</h6>
      {description && <p>{description}</p>}
      <MDBox className="tutorial-table-scroll">
        <table>
          <thead>
            <tr>
              {table.headers.map((header, index) => (
                <th key={index}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </MDBox>
    </>
  );
}

TutorialList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

TutorialTable.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  table: PropTypes.shape({
    headers: PropTypes.arrayOf(PropTypes.string).isRequired,
    rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  }).isRequired,
};

TutorialTable.defaultProps = {
  description: null,
};

function getTutorialResource(t, key) {
  return t("tutorial." + key, { returnObjects: true });
}

function getStepContent(stepIndex, t) {
  const resource = (key) => getTutorialResource(t, key);

  switch (stepIndex) {
    case 0:
      return (
        <MDBox>
          <h4>{t("tutorial.featureOverviewHeading")}</h4>
          <p>{t("tutorial.featureOverviewDescription")}</p>
          <p>{t("tutorial.helpHint")}</p>

          <h5>{t("tutorial.overviewHeading")}</h5>
          <p>{t("tutorial.overviewDescription")}</p>
          <h6>{t("tutorial.simplifiedTrendsHeading")}</h6>
          <TutorialList items={resource("overviewTrendItems")} />
          <p>{t("tutorial.overviewAnalysisLead")}</p>
          <TutorialList items={resource("overviewAnalysisItems")} />
          <p>{t("tutorial.overviewSubpageNote")}</p>

          <h5>{t("tutorial.degradationSoilingHeading")}</h5>
          <p>{t("tutorial.degradationSoilingDescription")}</p>
          <p>{t("tutorial.degradationSoilingAiNote")}</p>

          <h5>{t("tutorial.serviceLifeHeading")}</h5>
          <p>{t("tutorial.serviceLifeDescription")}</p>
          <p>{t("tutorial.serviceLifeBenchmark")}</p>
          <p>{t("tutorial.serviceLifeThreshold")}</p>
          <p>{t("tutorial.serviceLifeAiNote")}</p>

          <h5>{t("tutorial.reuseHeading")}</h5>
          <p>{t("tutorial.reuseDescription")}</p>
          <p>{t("tutorial.reuseSimulationNote")}</p>

          <h6>{t("tutorial.gridConnectedHeading")}</h6>
          <p>{t("tutorial.automaticallyDetectedParameters")}</p>
          <TutorialList items={resource("gridDetectedParameters")} />
          <p>{t("tutorial.userSelectableParameters")}</p>
          <TutorialList items={resource("gridSelectableParameters")} />
          <p>{t("tutorial.predictedResults")}</p>
          <TutorialList items={resource("gridPredictedResults")} />

          <h6>{t("tutorial.offGridHeading")}</h6>
          <p>{t("tutorial.automaticallyDetectedParameters")}</p>
          <TutorialList items={resource("offGridDetectedParameters")} />
          <p>{t("tutorial.userSelectableParameters")}</p>
          <TutorialList items={resource("offGridSelectableParameters")} />
          <p>{t("tutorial.predictedResults")}</p>
          <TutorialList items={resource("offGridPredictedResults")} />

          <h5>{t("tutorial.environmentalEconomicHeading")}</h5>
          <p>{t("tutorial.environmentalEconomicDescription")}</p>
          <p>{t("tutorial.environmentalEconomicFramework")}</p>

          <h6>{t("tutorial.environmentalImpactHeading")}</h6>
          <p>{t("tutorial.environmentalScenariosLead")}</p>
          <TutorialList items={resource("environmentalScenarios")} />
          <p>{t("tutorial.environmentalDetails")}</p>
          <p>{t("tutorial.environmentalResult")}</p>
          <p>{t("tutorial.environmentalReuseLink")}</p>

          <h6>{t("tutorial.economicValueHeading")}</h6>
          <p>{t("tutorial.economicValueDescription")}</p>
          <p>{t("tutorial.economicValueScenarios")}</p>
          <p>{t("tutorial.economicValueIndicators")}</p>
          <p>{t("tutorial.economicValueExclusion")}</p>
        </MDBox>
      );
    case 1:
      return (
        <MDBox>
          <p>{t("tutorial.preparationDescription")}</p>
          <h5>{t("tutorial.dataPreparationHeading")}</h5>
          <p>{t("tutorial.dataPreparationDescription")}</p>
          {resource("dataPreparationTypes").length > 0 && (
            <TutorialList items={resource("dataPreparationTypes")} />
          )}
          <p>{t("tutorial.dataTablesDescription")}</p>
          <TutorialTable
            title={t("tutorial.timeSeriesDataHeading")}
            description={t("tutorial.timeSeriesDownloadNote")}
            table={resource("timeSeriesTable")}
          />
          <TutorialTable title={t("tutorial.metadataHeading")} table={resource("metadataTable")} />
          <p>{t("tutorial.metadataSupplement")}</p>
        </MDBox>
      );
    case 2:
      return (
        <MDBox>
          <p>{t("tutorial.dataUploadDescription")}</p>
          <h5>{t("tutorial.fileManagerHeading")}</h5>
          <p>{t("tutorial.fileManagerDescription")}</p>
          <p>{t("tutorial.startAnalysisInstruction")}</p>
          <p>{t("tutorial.completionStatusInstruction")}</p>
        </MDBox>
      );
    default:
      return null;
  }
}

function Tutorial() {
  const { t } = useTranslation("pages");
  const { t: tHelp } = useTranslation("help");
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    t("tutorial.stepIntroduction"),
    t("tutorial.stepPreparation"),
    t("tutorial.stepDataUpload"),
  ];
  const isLastStep = activeStep === steps.length - 1;

  const handleNext = () => setActiveStep(activeStep + 1);
  const handleBack = () => setActiveStep(activeStep - 1);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={1} pb={8}>
        <Grid container justifyContent="center" sx={{ mt: { xs: 2, md: 3 }, mb: 4 }}>
          <Grid item xs={12}>
            <MDBox mt={{ xs: 2, md: 3 }} mb={{ xs: 4, md: 5 }} textAlign="center">
              <MDBox mb={0.5}>
                <MDTypography variant="h3" fontWeight="bold">
                  {t("tutorial.title")}
                </MDTypography>
              </MDBox>
              <MDTypography variant="button" color="text" fontWeight="regular">
                {t("tutorial.subtitle")}
              </MDTypography>
            </MDBox>
            <Card sx={{ position: "relative" }}>
              <HelpTooltip
                title={tHelp("tutorial.introduction")}
                sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
              />
              <MDBox mt={-3} mx={{ xs: 1, sm: 2 }} px={{ xs: 0, sm: 2 }}>
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel
                  sx={({ typography: { fontWeightBold, fontWeightMedium, size } }) => ({
                    "& .MuiStepLabel-label": {
                      mt: 1,
                      fontSize: { xs: size.xs, sm: size.sm },
                      fontWeight: fontWeightMedium,
                      lineHeight: 1.4,
                    },
                    "& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed": {
                      fontWeight: fontWeightBold,
                    },
                  })}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </MDBox>
              <MDBox px={{ xs: 2.5, sm: 4, md: 6 }} pt={{ xs: 4, md: 5 }} pb={{ xs: 3, md: 4 }}>
                <MDBox sx={tutorialContentStyles}>
                  {getStepContent(activeStep, t)}
                  <MDBox
                    mt={4}
                    pt={3}
                    width="100%"
                    display="flex"
                    justifyContent="space-between"
                    sx={({ palette: { grey } }) => ({ borderTop: "1px solid " + grey[200] })}
                  >
                    {activeStep === 0 ? (
                      <MDBox />
                    ) : (
                      <MDButton variant="outlined" color="dark" onClick={handleBack}>
                        {t("tutorial.previous")}
                      </MDButton>
                    )}
                    {isLastStep ? (
                      <MDBox />
                    ) : (
                      <MDButton variant="gradient" color="dark" onClick={handleNext}>
                        {t("tutorial.next")}
                      </MDButton>
                    )}
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tutorial;
